import {
    BooleanShape,
    CallExpressionShape,
    IdentifierShape,
    NumberShape,
    ObjectShape,
    Plugin,
    Shape,
} from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import {
    TRIGGER_INSTANCE_TABLE,
    TRIGGER_INSTANCE_API_NAME,
    SYS_VARIABLE_VALUE_TABLE,
    SYS_ELEMENT_MAPPING_TABLE,
    PROCESS_INPUT_TABLE,
    PARENT_RECORD_MAPPER_KEY,
    PARENT_RECORD,
} from '../constants/playbook-plugin-constants'
import { TRIGGER_TYPE_SYS_ID_TO_IDENTIFIER, INPUT_SYS_ID_TO_FLUENT } from '../constants/playbook-trigger-types'
import { TriggerInstanceShape } from '../utils/playbook-shapes'
import { getFactoryCallConfig, extractVariableSysId } from '../utils/DefinitionResolver'
import { transformStandardPills, type PillTransformContext } from '../utils/playbook-pill-transformer'
import { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'
import type { Record as SdkRecord } from '@servicenow/sdk-build-core'
import { isPlaybookUnsupported } from '../utils/transform-utils'

/**
 * Convert a trigger instance record to a CallExpressionShape (XML → Fluent).
 * Exported for use by the playbook definition plugin's declarations processing.
 */
export function triggerInstanceRecordToShape(
    record: SdkRecord,
    descendants: { query: (table: string) => SdkRecord[] }
): { success: true; value: CallExpressionShape } | { success: false } {
    // Read trigger_type GUID and resolve to PlaybookTriggerTypes identifier
    const triggerTypeGuid = record.get('trigger_type')?.ifString()?.getValue() ?? ''
    const identifier = TRIGGER_TYPE_SYS_ID_TO_IDENTIFIER[triggerTypeGuid]
    if (!identifier) {
        return { success: false }
    }

    // Build trigger type as e.g. PlaybookTriggerTypes.RecordCreate identifier reference
    const triggerTypeShape = new IdentifierShape({
        source: record,
        name: identifier,
    })

    // Build config shape with $id and label
    const configShape = record.transform(({ $ }) => ({
        $id: $.val(NowIdShape.from(record)),
        label: $.from('label'),
    }))

    // Query descendant sys_variable_value records for this trigger instance
    const triggerSysId = record.getId().getValue()
    const variableValueRecords = descendants.query(SYS_VARIABLE_VALUE_TABLE).filter((vv) => {
        const docKey = vv.get('document_key')?.ifString()?.getValue() ?? ''
        return docKey === triggerSysId
    })

    // Build trigger inputs by mapping sys_variable_value.variable (a sys_pd_trigger_input
    // glidevar sys_id) back to the input element name and creating a typed shape
    const inputProperties: Record<string, Shape> = {}

    for (const vvRecord of variableValueRecords) {
        const variableGuid = vvRecord.get('variable')?.ifString()?.getValue() ?? ''
        const value = vvRecord.get('value')?.ifString()?.getValue() ?? ''
        if (value === '') {
            continue
        }

        const resolved = INPUT_SYS_ID_TO_FLUENT[variableGuid]
        if (!resolved) {
            continue
        }

        switch (resolved.type) {
            case 'boolean':
                inputProperties[resolved.name] = new BooleanShape({
                    source: vvRecord,
                    value: value === 'true' || value === '1',
                })
                break
            case 'number':
                inputProperties[resolved.name] = new NumberShape({
                    source: vvRecord,
                    value: Number(value),
                })
                break
            default:
                inputProperties[resolved.name] = Shape.from(vvRecord, value)
                break
        }
    }

    const triggerInputsShape = new ObjectShape({
        source: record,
        properties: inputProperties,
    })

    // Reconstruct the mapper arrow: pills come from sys_element_mapping, hardcoded
    // values from sys_variable_value (joined to sys_pd_process_input by variable).
    const mapperArrow = buildMapperArrow(descendants, triggerSysId, record)

    const args: Shape[] = [triggerTypeShape, configShape, triggerInputsShape]
    if (mapperArrow) {
        args.push(mapperArrow)
    }

    return {
        success: true,
        value: new CallExpressionShape({
            source: record,
            callee: TRIGGER_INSTANCE_API_NAME,
            args,
        }),
    }
}

function buildMapperArrow(
    descendants: { query: (table: string) => SdkRecord[] },
    triggerSysId: string,
    source: SdkRecord
): Shape | undefined {
    // Pills are stored in sys_element_mapping (value = pill string).
    const elementMappings = descendants
        .query(SYS_ELEMENT_MAPPING_TABLE)
        .filter((em) => em.get('id')?.ifString()?.getValue() === triggerSysId)

    // Hardcoded values are stored in sys_variable_value referencing a
    // sys_pd_process_input glidevar. Pills also have a paired sys_variable_value
    // whose value mirrors the pill expression — we filter those out using pillFields.
    const variableValues = descendants
        .query(SYS_VARIABLE_VALUE_TABLE)
        .filter((vv) => vv.get('document_key')?.ifString()?.getValue() === triggerSysId)

    const pillFields = new Set<string>()
    const properties: Record<string, Shape> = {}
    const pillContext: PillTransformContext = {
        activityMap: new Map(), // empty for trigger context
    }

    for (const em of elementMappings) {
        const field = em.get('field')?.ifString()?.getValue()
        const value = em.get('value')?.ifString()?.getValue()
        if (field && value) {
            // parent_record is the platform field name; map to the Fluent key
            const fluentKey = field === PARENT_RECORD.ELEMENT ? PARENT_RECORD_MAPPER_KEY : field
            pillFields.add(fluentKey)
            // parent_record with value {{triggerRecord}} is the default, so skip it
            if (fluentKey === PARENT_RECORD_MAPPER_KEY && value === '{{triggerRecord}}') {
                continue
            }
            const shape = transformStandardPills(value, em, pillContext)
            if (shape) {
                properties[fluentKey] = shape
            }
        }
    }

    // Hardcoded values: VV records pointing to a sys_pd_process_input whose
    // element name isn't already a pill. The variable GUID maps back to the
    // process input record's element name.
    const processInputs = descendants.query(PROCESS_INPUT_TABLE)
    for (const vv of variableValues) {
        const variableSysId = vv.get('variable')?.ifString()?.getValue()
        const value = vv.get('value')?.ifString()?.getValue()
        if (!variableSysId || !value) {
            continue
        }
        const processInput = processInputs.find((pi) => pi.getId().getValue() === variableSysId)
        if (!processInput) {
            continue
        }
        const elementName = processInput.get('element')?.ifString()?.getValue()
        // parent_record is the platform field name; map to the Fluent key
        const fluentKey = elementName === PARENT_RECORD.ELEMENT ? PARENT_RECORD_MAPPER_KEY : elementName
        if (!fluentKey || pillFields.has(fluentKey)) {
            continue
        }
        properties[fluentKey] = Shape.from(vv, value)
    }

    if (Object.keys(properties).length === 0) {
        return undefined
    }

    // Emit `(trigger)` when any property in the returned object is a pill (its value references `trigger.current.*`).
    // `pillFields` may contain `PARENT_RECORD_MAPPER_KEY` from the default {{triggerRecord}} EM even though that entry
    // is skipped and never added to `properties`, so we check intersection with the actual output properties rather
    // than size of pillFields.
    const parameters = Object.keys(properties).some((k) => pillFields.has(k))
        ? [new IdentifierShape({ source, name: 'trigger' })]
        : []
    const returnObject = new ObjectShape({ source, properties })

    return new ArrowFunctionShape({
        source,
        parameters,
        returnValue: returnObject,
        implicitReturn: true,
    })
}

export const PlaybookTriggerInstancePlugin = Plugin.create({
    name: 'PlaybookTriggerInstancePlugin',
    records: {
        [TRIGGER_INSTANCE_TABLE]: {
            relationships: {
                [SYS_VARIABLE_VALUE_TABLE]: {
                    via: 'document_key',
                    descendant: true,
                },
                [SYS_ELEMENT_MAPPING_TABLE]: {
                    via: 'id',
                    descendant: true,
                },
            },
            toShape(record, { descendants, database }) {
                if (isPlaybookUnsupported(record, database)) {
                    return { success: false }
                }
                return triggerInstanceRecordToShape(record, descendants)
            },
        },
        [SYS_VARIABLE_VALUE_TABLE]: {
            coalesce: ['document_key', 'variable'],
        },
        [SYS_ELEMENT_MAPPING_TABLE]: {
            coalesce: ['id', 'field', 'table'],
        },
    },
    shapes: [
        {
            shape: TriggerInstanceShape,
            fileTypes: ['fluent'],
            async toRecord(triggerShape, { factory }) {
                const triggerType = triggerShape.getTriggerType()
                const config = triggerShape.getConfig()
                const triggerInputs = triggerShape.getTriggerInputs()

                // Resolve trigger type through the shape system to the .now.ts definition.
                // PlaybookTriggerTypes.RecordCreate → RecordCreate → PlaybookTriggerType({ $id, inputs })
                const triggerTypeConfig = getFactoryCallConfig(triggerType)
                if (!triggerTypeConfig) {
                    return { success: false }
                }

                const triggerTypeGuid = triggerTypeConfig.get('$id', false)?.ifString()?.getValue()
                if (!triggerTypeGuid) {
                    return { success: false }
                }

                const triggerTypeInputs = triggerTypeConfig.get('inputs', false)?.ifObject()

                // Read label and compute name
                const labelVal = config.get('label')?.ifString()?.getValue() ?? ''
                const name = labelVal
                    ? labelVal
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '_')
                          .replace(/^_|_$/g, '')
                    : ''

                const processDefinitionSysId = config.get('processDefinition')?.ifString()?.getValue() ?? ''

                // Create trigger instance record
                const triggerRecord = await factory.createRecord({
                    source: triggerShape,
                    table: TRIGGER_INSTANCE_TABLE,
                    explicitId: config.get('$id'),
                    properties: {
                        trigger_type: triggerTypeGuid,
                        label: labelVal,
                        name,
                        process_definition: processDefinitionSysId,
                    },
                })

                const childRecords: SdkRecord[] = []

                // Create sys_variable_value child records for each trigger input.
                for (const [elementName, shape] of triggerInputs.entries()) {
                    const inputShape = triggerTypeInputs?.get(elementName)
                    const variableDefGuid = inputShape ? extractVariableSysId(inputShape) : undefined
                    if (!variableDefGuid) {
                        continue
                    }

                    const value = shape.ifString()?.getValue() ?? String(shape.getValue?.() ?? '')

                    const vvRecord = await factory.createRecord({
                        source: shape,
                        table: SYS_VARIABLE_VALUE_TABLE,
                        properties: {
                            document: TRIGGER_INSTANCE_TABLE,
                            document_key: triggerRecord.getId().getValue(),
                            variable: variableDefGuid,
                            value,
                        },
                    })
                    childRecords.push(vvRecord)
                }

                // Create mapper records: sys_element_mapping for pills (paired with a
                // sys_variable_value whose value mirrors the pill expression so that
                // PAD's mandatory-field validation passes via getValue()), or an empty
                // sys_element_mapping paired with sys_variable_value for hardcoded values.
                const mapperMappings = triggerShape.getMapperMappings()
                const playbookInputSysIds = triggerShape.getPlaybookInputSysIds()
                const parentRecordInputSysId = triggerShape.getParentRecordInputSysId()

                // Handle parentRecord from the mapper (special key, not in playbookInputSysIds).
                let parentRecordSet = false
                const triggerSysId = triggerRecord.getId().getValue()
                const glideVarTable = `var__m_${PROCESS_INPUT_TABLE}_${processDefinitionSysId}`
                if (mapperMappings && parentRecordInputSysId) {
                    const parentRecordMapping = mapperMappings.get(PARENT_RECORD_MAPPER_KEY)
                    if (parentRecordMapping) {
                        parentRecordSet = true
                        if (parentRecordMapping.isPill) {
                            const emRecord = await factory.createRecord({
                                source: triggerShape,
                                table: SYS_ELEMENT_MAPPING_TABLE,
                                properties: {
                                    field: PARENT_RECORD.ELEMENT,
                                    id: triggerSysId,
                                    table: glideVarTable,
                                    value: parentRecordMapping.value,
                                },
                            })
                            childRecords.push(emRecord)
                            const vvRecord = await factory.createRecord({
                                source: triggerShape,
                                table: SYS_VARIABLE_VALUE_TABLE,
                                properties: {
                                    document: TRIGGER_INSTANCE_TABLE,
                                    document_key: triggerSysId,
                                    variable: parentRecordInputSysId,
                                    value: '',
                                },
                            })
                            childRecords.push(vvRecord)
                        } else {
                            const vvRecord = await factory.createRecord({
                                source: triggerShape,
                                table: SYS_VARIABLE_VALUE_TABLE,
                                properties: {
                                    document: TRIGGER_INSTANCE_TABLE,
                                    document_key: triggerSysId,
                                    variable: parentRecordInputSysId,
                                    value: parentRecordMapping.value,
                                },
                            })
                            childRecords.push(vvRecord)
                            const emRecord = await factory.createRecord({
                                source: triggerShape,
                                table: SYS_ELEMENT_MAPPING_TABLE,
                                properties: {
                                    field: PARENT_RECORD.ELEMENT,
                                    id: triggerSysId,
                                    table: glideVarTable,
                                    value: '',
                                },
                            })
                            childRecords.push(emRecord)
                        }
                    }
                }

                if (!parentRecordSet && triggerShape.getIsSameTableAsParent()) {
                    // Default parent_record to {{triggerRecord}} pill for same-table triggers
                    // (parentTable set and triggerTable === parentTable, no explicit parentRecord).
                    const emRecord = await factory.createRecord({
                        source: triggerShape,
                        table: SYS_ELEMENT_MAPPING_TABLE,
                        properties: {
                            field: PARENT_RECORD.ELEMENT,
                            id: triggerSysId,
                            table: glideVarTable,
                            value: '{{triggerRecord}}',
                        },
                    })
                    childRecords.push(emRecord)
                    if (parentRecordInputSysId) {
                        // Only set blank sys_variable_value if parentRecordInputSysId exists
                        const vvRecord = await factory.createRecord({
                            source: triggerShape,
                            table: SYS_VARIABLE_VALUE_TABLE,
                            properties: {
                                document: TRIGGER_INSTANCE_TABLE,
                                document_key: triggerSysId,
                                variable: parentRecordInputSysId,
                                value: '',
                            },
                        })
                        childRecords.push(vvRecord)
                    }
                }

                if (mapperMappings && processDefinitionSysId && playbookInputSysIds) {
                    const glideVarTable = `var__m_${PROCESS_INPUT_TABLE}_${processDefinitionSysId}`
                    const triggerSysId = triggerRecord.getId().getValue()
                    for (const [elementName, mapperValue] of mapperMappings) {
                        // parentRecord is handled above; skip it here
                        if (elementName === PARENT_RECORD_MAPPER_KEY) {
                            continue
                        }

                        const inputSysId = playbookInputSysIds.get(elementName)
                        if (!inputSysId) {
                            // Unknown input — diagnostics already flagged it upstream.
                            continue
                        }

                        if (mapperValue.isPill) {
                            const emRecord = await factory.createRecord({
                                source: triggerShape,
                                table: SYS_ELEMENT_MAPPING_TABLE,
                                properties: {
                                    field: elementName,
                                    id: triggerSysId,
                                    table: glideVarTable,
                                    value: mapperValue.value,
                                },
                            })
                            childRecords.push(emRecord)

                            const vvRecord = await factory.createRecord({
                                source: triggerShape,
                                table: SYS_VARIABLE_VALUE_TABLE,
                                properties: {
                                    document: TRIGGER_INSTANCE_TABLE,
                                    document_key: triggerSysId,
                                    variable: inputSysId,
                                    value: mapperValue.value,
                                },
                            })
                            childRecords.push(vvRecord)
                        } else {
                            const emRecord = await factory.createRecord({
                                source: triggerShape,
                                table: SYS_ELEMENT_MAPPING_TABLE,
                                properties: {
                                    field: elementName,
                                    id: triggerSysId,
                                    table: glideVarTable,
                                    value: '',
                                },
                            })
                            childRecords.push(emRecord)

                            const vvRecord = await factory.createRecord({
                                source: triggerShape,
                                table: SYS_VARIABLE_VALUE_TABLE,
                                properties: {
                                    document: TRIGGER_INSTANCE_TABLE,
                                    document_key: triggerSysId,
                                    variable: inputSysId,
                                    value: mapperValue.value,
                                },
                            })
                            childRecords.push(vvRecord)
                        }
                    }
                }

                return {
                    success: true,
                    value: triggerRecord.with(...childRecords),
                }
            },
        },
    ],
    docs: [],
})
